import React from "react";
import { Link } from "react-router-dom";

type Cta = {
  label: string;
  to: string;
  variant?: "primary" | "secondary";
  iconRight?: React.ReactNode;
};

type HeroProps = {
  title: string;
  subtitle?: string;
  heightClass?: string;   // ex: "h-[60vh]" ou "h-[40vh]"
  center?: boolean;       // centre le texte
  ctas?: Cta[];
  headerOffset?: number;  // pour décaler sous le header si besoin
  variant?: "subtle" | "brandDark" | "custom"; // look & feel
  bgClassName?: string;   // si variant = "custom", on passe sa classe fond
  children?: React.ReactNode;
};

const GradientHero: React.FC<HeroProps> = ({
  title,
  subtitle,
  heightClass = "h-[50vh]",
  center = true,
  ctas = [],
  headerOffset = 64,
  variant = "subtle",
  bgClassName,
  children
}) => {
  // Fond selon variante
  const renderBackground = () => {
    if (variant === "brandDark") {
      return (
        <>
          <div className="absolute inset-0 bg-primary" />
          {/* très léger vignettage pour la profondeur */}
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(closest-side,rgba(255,255,255,0.08),transparent_70%)]" />
        </>
      );
    }
    if (variant === "custom") {
      return <div className={`absolute inset-0 ${bgClassName || ""}`} />;
    }
    // Subtle par défaut : blanc → gris-50 + micro texture douce
    return (
      <>
        <div className="absolute inset-0 bg-gradient-to-b from-white to-gray-50" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(0,0,0,0.035)_1px,transparent_1.5px)] bg-[length:18px_18px]" />
      </>
    );
  };

  const baseTextColor = variant === "brandDark" ? "text-white" : "text-gray-900";
  const subTextColor = variant === "brandDark" ? "text-white/85" : "text-gray-700";

  return (
    <section
      className={`relative ${heightClass} flex items-center ${center ? "justify-center" : ""} overflow-hidden`}
      style={{ marginTop: headerOffset, minHeight: `calc(50vh - ${headerOffset}px)` }}
      aria-label="En-tête de page"
    >
      {renderBackground()}

      <div className="container relative z-10">
        <div className={`${center ? "text-center" : ""}`}>
          <h1 className={`text-4xl md:text-6xl font-bold mb-6 ${baseTextColor}`}>{title}</h1>
          {subtitle && (
            <p className={`text-lg md:text-2xl max-w-4xl mx-auto ${subTextColor}`}>
              {subtitle}
            </p>
          )}

          {!!ctas.length && (
            <div className={`mt-8 flex flex-col sm:flex-row gap-4 ${center ? "justify-center" : ""}`}>
              {ctas.map(({ label, to, variant = "primary", iconRight }, i) => (
                <Link
                  key={i}
                  to={to}
                  className={
                    variant === "primary"
                      ? "btn-primary inline-flex items-center"
                      : "btn-secondary inline-flex items-center"
                  }
                >
                  {label}
                  {iconRight && <span className="ml-2">{iconRight}</span>}
                </Link>
              ))}
            </div>
          )}

          {children}
        </div>
      </div>
    </section>
  );
};

export default GradientHero;
